import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RecurrenceSelector } from '../RecurrenceSelector';
import { AutomationFormData, Automation } from '../../types/automatisation.types';
import './AutomatisationForm.css';

interface AutomatisationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AutomationFormData) => void;
  initialData?: Automation | null;
  users: any[];
  isLoading?: boolean;
}

export const AutomatisationForm: React.FC<AutomatisationFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  users,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<AutomationFormData>({
    name: '',
    description: '',
    task_template: {
      message: '',
      assignedToUsers: [],
      assignedToRoles: [],
      criticity: 'medium'
    },
    recurrence_type: 'daily',
    recurrence_config: { hour: 9, minute: 0 },
    timezone: 'Europe/Brussels',
    skip_if_assignee_absent: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [assignmentType, setAssignmentType] = useState<'users' | 'roles' | 'unassigned'>('users');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description,
        task_template: initialData.task_template,
        recurrence_type: initialData.recurrence_type,
        recurrence_config: initialData.recurrence_config,
        start_date: initialData.start_date,
        end_date: initialData.end_date,
        max_executions: initialData.max_executions,
        timezone: initialData.timezone,
        skip_if_assignee_absent: initialData.skip_if_assignee_absent || false
      });

      // Set the assignment type based on existing data
      if (initialData.task_template.needs_assignment) {
        setAssignmentType('unassigned');
      } else if (initialData.task_template.assignedToRoles && initialData.task_template.assignedToRoles.length > 0) {
        setAssignmentType('roles');
      } else {
        setAssignmentType('users');
      }
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.task_template.message.trim()) {
      newErrors.message = 'Le message de la tâche est requis';
    }

    // Only require assignment if not unassigned type
    if (assignmentType !== 'unassigned' &&
        formData.task_template.assignedToUsers?.length === 0 &&
        formData.task_template.assignedToRoles?.length === 0) {
      newErrors.assignment = 'Veuillez assigner à au moins un utilisateur ou rôle';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="admin-automatisation-modal-overlay">
      <div className="admin-automatisation-modal">
        <div className="admin-automatisation-modal-header">
          <h2 className="admin-automatisation-modal-title">
            {initialData ? 'Modifier l\'automatisation' : 'Nouvelle automatisation'}
          </h2>
          <button onClick={onClose} className="admin-automatisation-modal-close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="admin-automatisation-form">
          {/* Name and Description */}
          <div className="admin-automatisation-form-section">
            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">
                Nom de l'automatisation *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="admin-automatisation-form-input"
                placeholder="Ex: Rappel hebdomadaire"
              />
              {errors.name && <span className="admin-automatisation-error">{errors.name}</span>}
            </div>

            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="admin-automatisation-form-textarea"
                rows={2}
                placeholder="Description de l'automatisation..."
              />
            </div>
          </div>

          {/* Task Template */}
          <div className="admin-automatisation-form-section">
            <h3 className="admin-automatisation-section-title">Configuration de la tâche</h3>
            
            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">Message de la tâche *</label>
              <textarea
                value={formData.task_template.message}
                onChange={(e) => setFormData({
                  ...formData,
                  task_template: { ...formData.task_template, message: e.target.value }
                })}
                className="admin-automatisation-form-textarea"
                rows={3}
                placeholder="Entrez le message de la tâche..."
              />
              {errors.message && <span className="admin-automatisation-error">{errors.message}</span>}
            </div>

            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">Priorité</label>
              <div className="admin-automatisation-priority-pills">
                <button
                  type="button"
                  className={`admin-automatisation-priority-pill low ${formData.task_template.criticity === 'low' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    task_template: { ...formData.task_template, criticity: 'low' }
                  })}
                >
                  <span className="admin-automatisation-priority-dot" style={{ background: '#22C55E' }}></span>
                  Faible
                </button>
                <button
                  type="button"
                  className={`admin-automatisation-priority-pill medium ${formData.task_template.criticity === 'medium' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    task_template: { ...formData.task_template, criticity: 'medium' }
                  })}
                >
                  <span className="admin-automatisation-priority-dot" style={{ background: '#6B7280' }}></span>
                  Moyenne
                </button>
                <button
                  type="button"
                  className={`admin-automatisation-priority-pill high ${formData.task_template.criticity === 'high' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    task_template: { ...formData.task_template, criticity: 'high' }
                  })}
                >
                  <span className="admin-automatisation-priority-dot" style={{ background: '#F97316' }}></span>
                  Haute
                </button>
                <button
                  type="button"
                  className={`admin-automatisation-priority-pill urgent ${formData.task_template.criticity === 'urgent' ? 'selected' : ''}`}
                  onClick={() => setFormData({
                    ...formData,
                    task_template: { ...formData.task_template, criticity: 'urgent' }
                  })}
                >
                  <span className="admin-automatisation-priority-dot" style={{ background: '#DC2626' }}></span>
                  Urgente
                </button>
              </div>
            </div>

            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">Assigner à *</label>
              <div className="admin-automatisation-assignment-tabs">
                <button
                  type="button"
                  className={`admin-automatisation-tab ${assignmentType === 'users' ? 'active' : ''}`}
                  onClick={() => {
                    setAssignmentType('users');
                    setFormData({
                      ...formData,
                      task_template: {
                        ...formData.task_template,
                        needs_assignment: false
                      }
                    });
                  }}
                >
                  Utilisateurs
                </button>
                <button
                  type="button"
                  className={`admin-automatisation-tab ${assignmentType === 'roles' ? 'active' : ''}`}
                  onClick={() => {
                    setAssignmentType('roles');
                    setFormData({
                      ...formData,
                      task_template: {
                        ...formData.task_template,
                        needs_assignment: false
                      }
                    });
                  }}
                >
                  Rôles
                </button>
                <button
                  type="button"
                  className={`admin-automatisation-tab ${assignmentType === 'unassigned' ? 'active' : ''}`}
                  onClick={() => {
                    setAssignmentType('unassigned');
                    setFormData({
                      ...formData,
                      task_template: {
                        ...formData.task_template,
                        assignedToUsers: [],
                        assignedToRoles: [],
                        needs_assignment: true
                      }
                    });
                  }}
                >
                  À assigner
                </button>
              </div>

              {assignmentType === 'users' ? (
                <select
                  multiple
                  value={formData.task_template.assignedToUsers || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({
                      ...formData,
                      task_template: {
                        ...formData.task_template,
                        assignedToUsers: selected,
                        assignedToRoles: []
                      }
                    });
                  }}
                  className="admin-automatisation-form-select-multiple"
                  size={4}
                >
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </option>
                  ))}
                </select>
              ) : assignmentType === 'roles' ? (
                <select
                  multiple
                  value={formData.task_template.assignedToRoles || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({
                      ...formData,
                      task_template: {
                        ...formData.task_template,
                        assignedToRoles: selected,
                        assignedToUsers: []
                      }
                    });
                  }}
                  className="admin-automatisation-form-select-multiple"
                  size={3}
                >
                  <option value="admin">Admin</option>
                  <option value="bureau">Bureau</option>
                  <option value="expert">Expert</option>
                </select>
              ) : (
                <div className="admin-automatisation-unassigned-info" style={{
                  padding: '16px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  color: '#1e40af'
                }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    Les tâches créées seront placées dans la catégorie "À assigner" et devront être assignées manuellement par un administrateur ou membre du bureau.
                  </p>
                </div>
              )}
              {errors.assignment && <span className="admin-automatisation-error">{errors.assignment}</span>}
            </div>
          </div>

          {/* Recurrence */}
          <div className="admin-automatisation-form-section">
            <h3 className="admin-automatisation-section-title">Récurrence</h3>
            <RecurrenceSelector
              recurrenceType={formData.recurrence_type}
              recurrenceConfig={formData.recurrence_config}
              onChange={(type, config) => setFormData({
                ...formData,
                recurrence_type: type,
                recurrence_config: config
              })}
            />
          </div>

          {/* Advanced Options */}
          <div className="admin-automatisation-form-section">
            <h3 className="admin-automatisation-section-title">Options avancées</h3>
            
            <div className="admin-automatisation-form-row">
              <div className="admin-automatisation-form-group">
                <label className="admin-automatisation-form-label">Date de début</label>
                <input
                  type="date"
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="admin-automatisation-form-input"
                />
              </div>

              <div className="admin-automatisation-form-group">
                <label className="admin-automatisation-form-label">Date de fin</label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="admin-automatisation-form-input"
                />
              </div>
            </div>

            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label">Nombre max d'exécutions</label>
              <input
                type="number"
                min="1"
                value={formData.max_executions || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  max_executions: e.target.value ? parseInt(e.target.value) : undefined
                })}
                className="admin-automatisation-form-input"
                placeholder="Illimité"
              />
            </div>

            <div className="admin-automatisation-form-group">
              <label className="admin-automatisation-form-label-inline" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.skip_if_assignee_absent || false}
                  onChange={(e) => setFormData({
                    ...formData,
                    skip_if_assignee_absent: e.target.checked
                  })}
                  style={{ width: '16px', height: '16px', accentColor: '#ef5343' }}
                />
                <span>Ne pas exécuter si l'assigné est absent</span>
              </label>
              {formData.skip_if_assignee_absent && (
                <p style={{
                  margin: '6px 0 0 26px',
                  fontSize: '12px',
                  color: '#6B7280'
                }}>
                  L'exécution sera ignorée si tous les utilisateurs assignés sont absents (jour de congé ou planning non actif).
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="admin-automatisation-form-actions">
            <button
              type="button"
              onClick={onClose}
              className="admin-automatisation-btn-cancel"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="admin-automatisation-btn-submit"
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : (initialData ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};